import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
  BookOpenIcon,
  TagIcon,
  ClockIcon,
  EyeIcon,
  StarIcon,
  FolderIcon,
  QuestionMarkCircleIcon,
  LightBulbIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import {
  DocumentTextIcon as DocumentTextIconSolid,
  HeartIcon as HeartIconSolid
} from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const KnowledgeBase = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');

  // Mock categories data
  const categories = [
    {
      id: 'getting-started',
      name: 'Getting Started',
      icon: BookOpenIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      articleCount: 12,
      description: 'Essential guides for new employees'
    },
    {
      id: 'account-access',
      name: 'Account & Access',
      icon: QuestionMarkCircleIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      articleCount: 18,
      description: 'Login, passwords, and system access'
    },
    {
      id: 'hr-policies',
      name: 'HR Policies',
      icon: DocumentTextIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      articleCount: 25,
      description: 'Leave, benefits, and workplace policies'
    },
    {
      id: 'it-support',
      name: 'IT Support',
      icon: LightBulbIcon,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      articleCount: 32,
      description: 'Technical help and troubleshooting'
    },
    {
      id: 'training',
      name: 'Training & Learning',
      icon: InformationCircleIcon,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
      articleCount: 15,
      description: 'Course enrollment and learning resources'
    },
    {
      id: 'facilities',
      name: 'Facilities',
      icon: ExclamationTriangleIcon,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      articleCount: 8,
      description: 'Office equipment and building services'
    }
  ];

  // Mock articles data
  const articles = [
    {
      id: 1,
      title: 'How to Reset Your Password',
      category: 'Account & Access',
      categoryId: 'account-access',
      description: 'Step-by-step guide to reset your account password and regain access to all systems.',
      content: 'This comprehensive guide will walk you through the password reset process...',
      views: 1250,
      helpful: 95,
      lastUpdated: '2024-07-20T10:00:00Z',
      readTime: '3 min read',
      difficulty: 'Beginner',
      tags: ['password', 'login', 'security', 'troubleshooting'],
      featured: true
    },
    {
      id: 2,
      title: 'Leave Application Process',
      category: 'HR Policies',
      categoryId: 'hr-policies',
      description: 'Complete guide on how to apply for different types of leave including vacation, sick, and personal time.',
      content: 'Learn about the leave application process, approval workflows, and important deadlines...',
      views: 890,
      helpful: 87,
      lastUpdated: '2024-07-18T14:30:00Z',
      readTime: '5 min read',
      difficulty: 'Beginner',
      tags: ['leave', 'vacation', 'hr', 'approval'],
      featured: false
    },
    {
      id: 3,
      title: 'IT Equipment Request Guidelines',
      category: 'IT Support',
      categoryId: 'it-support',
      description: 'How to request new equipment, report hardware issues, and understand the approval process.',
      content: 'This article covers the complete process for requesting IT equipment...',
      views: 650,
      helpful: 92,
      lastUpdated: '2024-07-19T09:15:00Z',
      readTime: '4 min read',
      difficulty: 'Intermediate',
      tags: ['equipment', 'hardware', 'request', 'approval'],
      featured: true
    },
    {
      id: 4,
      title: 'Training Course Enrollment',
      category: 'Training & Learning',
      categoryId: 'training',
      description: 'Instructions for enrolling in mandatory and optional training courses through the learning portal.',
      content: 'Discover how to browse, enroll, and track your progress in training courses...',
      views: 580,
      helpful: 89,
      lastUpdated: '2024-07-17T16:45:00Z',
      readTime: '6 min read',
      difficulty: 'Beginner',
      tags: ['training', 'courses', 'enrollment', 'learning'],
      featured: false
    },
    {
      id: 5,
      title: 'Setting Up Two-Factor Authentication',
      category: 'Account & Access',
      categoryId: 'account-access',
      description: 'Enhance your account security by enabling two-factor authentication on all your work accounts.',
      content: 'Learn how to set up and use two-factor authentication for enhanced security...',
      views: 445,
      helpful: 94,
      lastUpdated: '2024-07-21T11:20:00Z',
      readTime: '7 min read',
      difficulty: 'Intermediate',
      tags: ['2fa', 'security', 'authentication', 'setup'],
      featured: true
    },
    {
      id: 6,
      title: 'Employee Benefits Overview',
      category: 'HR Policies',
      categoryId: 'hr-policies',
      description: 'Comprehensive overview of all employee benefits including health insurance, retirement plans, and perks.',
      content: 'Get familiar with all the benefits available to Apollo Healthcare employees...',
      views: 720,
      helpful: 91,
      lastUpdated: '2024-07-16T13:00:00Z',
      readTime: '8 min read',
      difficulty: 'Beginner',
      tags: ['benefits', 'insurance', 'retirement', 'perks'],
      featured: false
    },
    {
      id: 7,
      title: 'Troubleshooting Common Login Issues',
      category: 'IT Support',
      categoryId: 'it-support',
      description: 'Solutions for the most common login problems including browser issues, cache problems, and connectivity.',
      content: 'This troubleshooting guide covers the most frequent login issues and their solutions...',
      views: 980,
      helpful: 88,
      lastUpdated: '2024-07-22T08:30:00Z',
      readTime: '5 min read',
      difficulty: 'Intermediate',
      tags: ['login', 'troubleshooting', 'browser', 'cache'],
      featured: true
    },
    {
      id: 8,
      title: 'Office Space and Desk Booking',
      category: 'Facilities',
      categoryId: 'facilities',
      description: 'How to book meeting rooms, hot desks, and other office spaces using the facilities management system.',
      content: 'Learn how to efficiently book and manage office spaces for optimal productivity...',
      views: 340,
      helpful: 86,
      lastUpdated: '2024-07-15T15:45:00Z',
      readTime: '4 min read',
      difficulty: 'Beginner',
      tags: ['booking', 'facilities', 'meeting-rooms', 'desk'],
      featured: false
    }
  ];

  // Filter articles based on search and category
  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || article.categoryId === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Featured articles
  const featuredArticles = articles.filter(article => article.featured);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return 'text-green-600 bg-green-100';
      case 'intermediate':
        return 'text-yellow-600 bg-yellow-100';
      case 'advanced':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Custom Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center justify-center">
                <DocumentTextIconSolid className="w-6 h-6 sm:w-8 sm:h-8 text-apollo-primary mr-2 sm:mr-3" />
                Knowledge Base
              </h1>
              <p className="mt-2 text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Find answers to common questions and learn how to use Apollo Healthcare systems
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Search Bar */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 sm:w-6 sm:h-6 absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search for articles, topics, or keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 sm:pl-12 pr-4 py-3 sm:py-4 w-full border border-gray-300 rounded-xl focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary text-base sm:text-lg"
              />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-gray-600">Popular searches:</span>
                {['password reset', 'leave application', 'training enrollment', 'IT support'].map((term) => (
                  <button
                    key={term}
                    onClick={() => setSearchQuery(term)}
                    className="px-3 py-1 bg-apollo-accent/10 text-apollo-accent rounded-full text-sm hover:bg-apollo-accent/20 transition-colors"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center">
            <FolderIcon className="w-5 h-5 sm:w-6 sm:h-6 text-apollo-accent mr-2" />
            Browse by Category
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`${category.bgColor} rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 group text-left ${
                  selectedCategory === category.id ? 'ring-2 ring-apollo-primary' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <category.icon className={`w-8 h-8 ${category.color} mb-3`} />
                    <h3 className="font-bold text-gray-900 mb-1">{category.name}</h3>
                    <p className="text-sm text-gray-600 mb-2">{category.description}</p>
                    <p className="text-xs font-medium text-gray-500">
                      {category.articleCount} articles
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Featured Articles */}
        {searchQuery === '' && selectedCategory === 'all' && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <FireIcon className="w-6 h-6 text-orange-500 mr-2" />
              Featured Articles
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {featuredArticles.slice(0, 4).map((article) => (
                <div key={article.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <span className="px-3 py-1 bg-apollo-accent/10 text-apollo-accent rounded-full text-xs font-medium">
                        {article.category}
                      </span>
                      <div className="flex items-center space-x-1">
                        <StarIcon className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm text-gray-600">Featured</span>
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-apollo-primary transition-colors cursor-pointer">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{article.description}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <EyeIcon className="w-4 h-4 mr-1" />
                          <span>{article.views}</span>
                        </div>
                        <div className="flex items-center">
                          <HeartIconSolid className="w-4 h-4 text-red-500 mr-1" />
                          <span>{article.helpful}%</span>
                        </div>
                        <span>{article.readTime}</span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${getDifficultyColor(article.difficulty)}`}>
                        {article.difficulty}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Articles List */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              {searchQuery || selectedCategory !== 'all' ? 'Search Results' : 'All Articles'}
              <span className="text-gray-500 font-normal ml-2">
                ({filteredArticles.length} articles)
              </span>
            </h2>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-primary focus:border-apollo-primary text-sm"
              >
                <option value="relevance">Relevance</option>
                <option value="popularity">Most Popular</option>
                <option value="newest">Newest</option>
                <option value="helpful">Most Helpful</option>
              </select>
            </div>
          </div>

          {/* Filter Tags */}
          {(searchQuery || selectedCategory !== 'all') && (
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-sm text-gray-600">Active filters:</span>
              {searchQuery && (
                <span className="px-3 py-1 bg-apollo-primary/10 text-apollo-primary rounded-full text-sm flex items-center">
                  Search: "{searchQuery}"
                  <button
                    onClick={() => setSearchQuery('')}
                    className="ml-2 text-apollo-primary hover:text-apollo-secondary"
                  >
                    ×
                  </button>
                </span>
              )}
              {selectedCategory !== 'all' && (
                <span className="px-3 py-1 bg-apollo-accent/10 text-apollo-accent rounded-full text-sm flex items-center">
                  Category: {categories.find(c => c.id === selectedCategory)?.name}
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className="ml-2 text-apollo-accent hover:text-apollo-primary"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          )}

          <div className="space-y-4">
            {filteredArticles.map((article) => (
              <div key={article.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="px-3 py-1 bg-apollo-accent/10 text-apollo-accent rounded-full text-xs font-medium">
                          {article.category}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs ${getDifficultyColor(article.difficulty)}`}>
                          {article.difficulty}
                        </span>
                        {article.featured && (
                          <div className="flex items-center space-x-1">
                            <StarIcon className="w-3 h-3 text-yellow-500" />
                            <span className="text-xs text-yellow-600">Featured</span>
                          </div>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-apollo-primary transition-colors cursor-pointer">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 mb-3 line-clamp-2">{article.description}</p>
                      
                      {/* Tags */}
                      <div className="flex items-center space-x-2 mb-3">
                        <TagIcon className="w-4 h-4 text-gray-400" />
                        <div className="flex flex-wrap gap-1">
                          {article.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                          {article.tags.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                              +{article.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center">
                            <EyeIcon className="w-4 h-4 mr-1" />
                            <span>{article.views} views</span>
                          </div>
                          <div className="flex items-center">
                            <HeartIconSolid className="w-4 h-4 text-red-500 mr-1" />
                            <span>{article.helpful}% helpful</span>
                          </div>
                          <div className="flex items-center">
                            <ClockIcon className="w-4 h-4 mr-1" />
                            <span>{article.readTime}</span>
                          </div>
                        </div>
                        <span>Updated {formatDate(article.lastUpdated)}</span>
                      </div>
                    </div>
                    <Link
                      to={`/support/knowledge-base/${article.id}`}
                      className="ml-4 p-2 text-gray-400 hover:text-apollo-primary transition-colors"
                    >
                      <ArrowRightIcon className="w-5 h-5" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Empty State */}
        {filteredArticles.length === 0 && (
          <div className="text-center py-12">
            <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No articles found</h3>
            <p className="text-gray-600 mb-6">
              Try adjusting your search terms or browse by category to find relevant articles.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
                className="px-4 py-2 bg-apollo-primary text-white rounded-lg hover:bg-apollo-secondary transition-colors"
              >
                Clear Filters
              </button>
              <Link
                to="/support/contact"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Contact Support
              </Link>
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-12 bg-gradient-to-r from-apollo-primary to-apollo-accent rounded-xl text-white p-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Still need help?</h2>
            <p className="text-apollo-secondary/20 mb-6">
              Can't find what you're looking for? Our support team is here to help you.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                to="/support/tickets/create"
                className="px-6 py-3 bg-white text-apollo-primary rounded-lg hover:bg-gray-100 transition-colors font-medium"
              >
                Create Support Ticket
              </Link>
              <Link
                to="/support/chat"
                className="px-6 py-3 border border-white text-white rounded-lg hover:bg-white hover:text-apollo-primary transition-colors font-medium"
              >
                Live Chat Support
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default KnowledgeBase;
